import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Button,
  Snackbar,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { format, startOfWeek, addWeeks, subWeeks, addDays, parseISO, isValid } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { AdminAvailabilityEditor } from '../components/AdminAvailabilityEditor';
import { adminApiService } from '../services/api';
import { useAuth } from '../auth/AuthProvider';
import { AdminRole } from '../auth/roles';
import { SchedulingData, SchedulingReporter, StatePreference, DayAvailability } from '../types';
import { exportSchedulingToExcel } from '../utils/excelExport';
import { HourSlotsEncoder } from '../utils/hourSlotsEncoder';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const Scheduling: React.FC = () => {
  const { hasRole } = useAuth();
  const isSchedulingAdmin = hasRole(AdminRole.SCHEDULING_ADMIN);
  const isAssigner = hasRole(AdminRole.ASSIGNER);
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialWeek = (): Date => {
    const weekParam = searchParams.get('week');
    if (weekParam) {
      const parsed = parseISO(weekParam);
      if (isValid(parsed)) return startOfWeek(parsed, { weekStartsOn: 1 });
    }
    return startOfWeek(new Date(), { weekStartsOn: 1 });
  };

  const [currentWeek, setCurrentWeek] = useState<Date>(getInitialWeek);
  const [selectedState, setSelectedState] = useState<string>(searchParams.get('state') || 'all');
  const [includeNonPrimary, setIncludeNonPrimary] = useState<boolean>(
    searchParams.get('includeNonPrimary') === 'true'
  );
  const [schedulingData, setSchedulingData] = useState<SchedulingData | null>(null);
  const [myStates, setMyStates] = useState<string[]>([]);
  const [preselectApplied, setPreselectApplied] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorReporter, setEditorReporter] = useState<SchedulingReporter | null>(null);
  const [editorDate, setEditorDate] = useState<Date>(new Date());
  const [editorDayAvailability, setEditorDayAvailability] = useState<DayAvailability | undefined>(undefined);
  const [editorReadOnly, setEditorReadOnly] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  // Load "my assigner" info on mount to pre-select state filter for single-state assigners.
  useEffect(() => {
    if (preselectApplied) return;
    let cancelled = false;
    (async () => {
      try {
        const meRes = await adminApiService.getMyAssigner();
        if (cancelled) return;
        if (meRes.success && meRes.data) {
          const states = meRes.data.states || [];
          setMyStates(states);
          if (
            isAssigner && !isSchedulingAdmin &&
            states.length === 1 &&
            !searchParams.get('state')
          ) {
            setSelectedState(states[0]);
          }
        }
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setPreselectApplied(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssigner, isSchedulingAdmin, preselectApplied]);

  // Sync filter state to URL search params
  useEffect(() => {
    const params: Record<string, string> = {};
    const defaultWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
    if (format(currentWeek, 'yyyy-MM-dd') !== format(defaultWeek, 'yyyy-MM-dd')) {
      params.week = format(currentWeek, 'yyyy-MM-dd');
    }
    if (selectedState !== 'all') {
      params.state = selectedState;
    }
    if (includeNonPrimary) {
      params.includeNonPrimary = 'true';
    }
    setSearchParams(params, { replace: true });
  }, [currentWeek, selectedState, includeNonPrimary, setSearchParams]);

  const loadSchedulingData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const weekStart = format(currentWeek, 'yyyy-MM-dd');
      const state = selectedState === 'all' ? undefined : selectedState;
      const primaryOnly = !includeNonPrimary;

      const response = await adminApiService.getSchedulingData(weekStart, state, primaryOnly);

      if (response.success && response.data) {
        setSchedulingData(response.data);
      } else {
        setError(response.error || 'Failed to load scheduling data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load scheduling data');
    } finally {
      setLoading(false);
    }
  }, [currentWeek, selectedState, includeNonPrimary]);

  useEffect(() => {
    loadSchedulingData();
  }, [loadSchedulingData]);

  const goToPreviousWeek = () => setCurrentWeek(prev => subWeeks(prev, 1));
  const goToNextWeek = () => setCurrentWeek(prev => addWeeks(prev, 1));

  const handleExportToExcel = () => {
    if (!schedulingData) return;
    exportSchedulingToExcel(schedulingData, currentWeek);
  };

  const renderReporterStates = (statePreferences: StatePreference[]) => {
    if (!statePreferences || statePreferences.length === 0) return '—';
    return statePreferences.map((pref, i) => (
      <React.Fragment key={pref.state}>
        {i > 0 && ', '}
        {pref.isPrimary ? <strong>{pref.state}</strong> : pref.state}
      </React.Fragment>
    ));
  };

  // Check if the current user can edit a specific reporter
  const canEditReporter = (reporter: SchedulingReporter): boolean => {
    if (isSchedulingAdmin) return true;
    if (!isAssigner) return false;
    const primary = reporter.statePreferences?.find(p => p.isPrimary)?.state;
    return !!primary && myStates.includes(primary);
  };

  const getAvailabilityStatus = (reporter: SchedulingReporter, day: keyof typeof reporter.weekAvailability) => {
    const dayAvailability = reporter.weekAvailability[day];

    if (!dayAvailability || !dayAvailability.isAvailable) {
      if (dayAvailability?.unavailable) {
        return { available: false, type: 'unavailable', jobTypes: [] as string[], notes: dayAvailability.notes };
      } else if (dayAvailability?.empty) {
        return { available: false, type: 'empty', jobTypes: [] as string[], notes: dayAvailability.notes };
      }
      return { available: false, type: 'unavailable', jobTypes: [] as string[] };
    }

    const jobTypes: string[] = dayAvailability.jobTypes || [];
    const timeBlocks = dayAvailability.timeBlocks ||
      (dayAvailability.hourSlots ? HourSlotsEncoder.getTimeBlocks(dayAvailability.hourSlots) : []);
    const formattedTime = timeBlocks.length > 0
      ? HourSlotsEncoder.formatTimeBlocks(timeBlocks)
      : (dayAvailability.startTime && dayAvailability.endTime
        ? `${formatTime(dayAvailability.startTime)}-${formatTime(dayAvailability.endTime)}`
        : undefined);

    return {
      available: true,
      type: 'available',
      jobTypes,
      timeRange: formattedTime,
      notes: dayAvailability.notes
    };
  };

  const formatTime = (timeStr: string): string => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    let hour = parseInt(hours, 10);
    const min = parseInt(minutes, 10);
    if (hour === 0) hour = 12;
    else if (hour > 12) hour = hour - 12;
    return min === 0 ? hour.toString() : `${hour}:${minutes.padStart(2, '0')}`;
  };

  const getJobTypeIndicator = (jobTypes: string[]): string => {
    const hasVirtual = jobTypes.includes('virtual');
    const hasInPerson = jobTypes.includes('in-person');
    if (hasVirtual && hasInPerson) return '';
    if (hasVirtual) return 'V';
    if (hasInPerson) return 'IP';
    return '';
  };

  // Open editor for a cell click
  const handleCellClick = (reporter: SchedulingReporter, dayIndex: number) => {
    const dayKey = weekdayKeys[dayIndex];
    const dayAvail = reporter.weekAvailability[dayKey];
    const cellDate = addDays(currentWeek, dayIndex);
    const canEdit = canEditReporter(reporter);

    setEditorReporter(reporter);
    setEditorDate(cellDate);
    setEditorDayAvailability(dayAvail);
    setEditorReadOnly(!canEdit);
    setEditorOpen(true);
  };

  const handleEditorSave = () => {
    setEditorOpen(false);
    setSnackbar({ open: true, message: 'Availability updated', severity: 'success' });
    loadSchedulingData();
  };

  const renderAvailabilityCell = (reporter: SchedulingReporter, day: keyof typeof reporter.weekAvailability, dayIndex: number) => {
    const status = getAvailabilityStatus(reporter, day);
    const canEdit = canEditReporter(reporter);

    const cellSx = {
      p: 1,
      textAlign: 'center' as const,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: canEdit ? 'action.hover' : 'rgba(0,0,0,0.02)',
      },
      transition: 'background-color 0.15s',
    };

    if (!status.available) {
      if (status.type === 'empty') {
        return (
          <TableCell key={day} sx={cellSx} onClick={() => handleCellClick(reporter, dayIndex)}>
            <Box sx={{ color: 'text.secondary' }}>
              {status.notes ? (
                <Tooltip title={status.notes}>
                  <Typography variant="caption" sx={{ fontSize: '1.2rem' }}>*</Typography>
                </Tooltip>
              ) : ''}
            </Box>
          </TableCell>
        );
      }
      return (
        <TableCell key={day} sx={cellSx} onClick={() => handleCellClick(reporter, dayIndex)}>
          <Box sx={{ color: 'text.disabled' }}>
            <Typography variant="caption" sx={{ fontSize: '0.875rem' }}>
              Unavailable
              {status.notes && (
                <Tooltip title={status.notes}><span style={{ marginLeft: '4px' }}>*</span></Tooltip>
              )}
            </Typography>
          </Box>
        </TableCell>
      );
    }

    const displayText = status.timeRange || getJobTypeIndicator(status.jobTypes);
    return (
      <TableCell key={day} sx={cellSx} onClick={() => handleCellClick(reporter, dayIndex)}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {displayText && (
              <Typography variant="caption" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {displayText}
              </Typography>
            )}
            {status.notes && (
              <Tooltip title={status.notes}>
                <Typography variant="caption" sx={{ fontSize: '1.2rem', color: 'warning.main' }}>*</Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
      </TableCell>
    );
  };

  const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const weekdayKeys: (keyof SchedulingReporter['weekAvailability'])[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

  if (loading) {
    return (
      <Layout pageTitle="Scheduling">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>Loading scheduling data...</Typography>
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout pageTitle="Scheduling">
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      </Layout>
    );
  }

  return (
    <Layout pageTitle="Reporter Scheduling">
      <Box>
        {/* Controls & Summary */}
        <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, px: 2, py: 1.5 }}>
          {/* Week Navigation */}
          <Select
            value=""
            size="small"
            readOnly
            IconComponent={() => null}
            renderValue={() => 'Week: ' + format(currentWeek, 'MMM dd') + ' - ' + format(addDays(addWeeks(currentWeek, 1), -1), 'MMM dd, yyyy')}
            displayEmpty
            sx={{ minWidth: 220, pr: 0 }}
            startAdornment={
              <IconButton onClick={goToPreviousWeek} size="small" sx={{ ml: -1, mr: -0.5 }}><ChevronLeftIcon fontSize="small" /></IconButton>
            }
            endAdornment={
              <IconButton onClick={goToNextWeek} size="small" sx={{ mr: -0.5 }}><ChevronRightIcon fontSize="small" /></IconButton>
            }
          />

          {/* State Filter */}
          <Select
            value={selectedState}
            size="small"
            onChange={(e) => setSelectedState(e.target.value)}
            sx={{ minWidth: 120 }}
            renderValue={(val) => `State: ${val === 'all' ? 'All' : val}`}
          >
            <MenuItem value="all">All States</MenuItem>
            {US_STATES.map((state) => (
              <MenuItem key={state} value={state}>{state}</MenuItem>
            ))}
          </Select>

          {/* Primary-only toggle */}
          <FormControlLabel
            control={
              <Checkbox
                checked={includeNonPrimary}
                onChange={(e) => setIncludeNonPrimary(e.target.checked)}
                size="small"
              />
            }
            label="Include non-primary"
            disabled={selectedState === 'all'}
            sx={{ ml: 0 }}
          />

          {/* Spacer + Export */}
          {(isSchedulingAdmin || isAssigner) && (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportToExcel}
                disabled={!schedulingData || schedulingData.reporters.length === 0}
              >
                Download Excel
              </Button>
            </>
          )}
        </Paper>

        {/* Scheduling Table */}
        {schedulingData && schedulingData.reporters.length > 0 ? (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>States</TableCell>
                  {weekdays.map((day) => (
                    <TableCell key={day} sx={{
                      fontWeight: 'bold',
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      p: 1,
                      minWidth: 70
                    }}>
                      {day.substring(0, 3)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {schedulingData.reporters.map((reporter) => {
                  const editable = canEditReporter(reporter);
                  return (
                    <TableRow
                      key={reporter.reporterId}
                      sx={{
                        backgroundColor: editable ? 'inherit' : 'rgba(0,0,0,0.02)',
                      }}
                    >
                      <TableCell sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                          {reporter.name || `Reporter ${reporter.reporterId}`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {reporter.location || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {reporter.email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {renderReporterStates(reporter.statePreferences)}
                        </Typography>
                      </TableCell>
                      {weekdayKeys.map((day, dayIndex) => renderAvailabilityCell(reporter, day, dayIndex))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info">No reporters found for the selected criteria.</Alert>
        )}
        {/* Legend */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', color: 'text.secondary' }}>
          <Typography variant="caption"><strong>V</strong> Virtual only</Typography>
          <Typography variant="caption"><strong>IP</strong> In-person only</Typography>
          <Typography variant="caption"><strong>*</strong> Has notes</Typography>
          <Typography variant="caption">Click a cell to {isSchedulingAdmin || isAssigner ? 'edit' : 'view'}</Typography>
        </Box>
      </Box>

      {/* Availability Editor Drawer */}
      <AdminAvailabilityEditor
        open={editorOpen}
        reporter={editorReporter}
        date={editorDate}
        dayAvailability={editorDayAvailability}
        onClose={() => setEditorOpen(false)}
        onSave={handleEditorSave}
        readOnly={editorReadOnly}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};
